const API_BASE = 'https://statsapi.mlb.com/api/v1';
const OFFICIAL_GAME_TYPES = new Set(['R', 'F', 'D', 'L', 'W']);

const makeSlug = (game) => {
  const date = game.officialDate || game.gameDate?.slice(0, 10);
  const away = game.teams?.away?.team?.abbreviation || 'away';
  const home = game.teams?.home?.team?.abbreviation || 'home';

  if (!date) return null;

  const gameNumber = Number(game.gameNumber || 1);
  const doubleHeader =
    game.doubleHeader === 'Y' ||
    game.doubleHeader === 'S' ||
    gameNumber > 1;
  const suffix = doubleHeader ? `-g${gameNumber}` : '';

  return `${date}-${away.toLowerCase()}-at-${home.toLowerCase()}${suffix}`;
};

const mapScheduleGame = (game) => ({
  gamePk: game.gamePk,
  slug: makeSlug(game),
  officialDate: game.officialDate || game.gameDate?.slice(0, 10) || '',
  date: game.gameDate || game.officialDate || '',
  venue: game.venue?.name || '',
  gameType: game.gameType || 'R',
  gameNumber: game.gameNumber || 1,
  doubleHeader: game.doubleHeader || 'N',
  status: game.status?.detailedState || 'Final',
  away: {
    name: game.teams?.away?.team?.name || '',
    abbr: game.teams?.away?.team?.abbreviation || '',
    score: game.teams?.away?.score ?? '',
    record: game.teams?.away?.leagueRecord || null
  },
  home: {
    name: game.teams?.home?.team?.name || '',
    abbr: game.teams?.home?.team?.abbreviation || '',
    score: game.teams?.home?.score ?? '',
    record: game.teams?.home?.leagueRecord || null
  },
  decisions: game.decisions || null
});

export const getGameSeason = (game) => {
  const source = game?.officialDate || game?.date || game?.gameDate;
  const year = Number(String(source || '').slice(0, 4));
  return Number.isFinite(year) ? year : null;
};

export const mergeSeasonGames = (...collections) => {
  const gamesByPk = new Map();

  collections.flat().filter(Boolean).forEach((game) => {
    if (!game?.gamePk) return;

    const key = String(game.gamePk);
    const previous = gamesByPk.get(key) || {};

    gamesByPk.set(key, {
      ...previous,
      ...game,
      away: {
        ...(previous.away || {}),
        ...(game.away || {})
      },
      home: {
        ...(previous.home || {}),
        ...(game.home || {})
      },
      decisions: game.decisions || previous.decisions || null
    });
  });

  return [...gamesByPk.values()].sort((a, b) => {
    const dateDifference =
      new Date(b.date || b.officialDate) -
      new Date(a.date || a.officialDate);

    if (dateDifference !== 0) return dateDifference;
    return Number(b.gamePk) - Number(a.gamePk);
  });
};

export async function fetchCompletedSeasonGames(
  season,
  teamId = 111,
  signal
) {
  const params = new URLSearchParams({
    sportId: '1',
    teamId: String(teamId),
    season: String(season),
    hydrate: 'team,linescore,decisions'
  });

  const response = await fetch(`${API_BASE}/schedule?${params}`, { signal });

  if (!response.ok) {
    throw new Error(`The ${season} MLB schedule could not be loaded.`);
  }

  const data = await response.json();

  return (data.dates || [])
    .flatMap((date) => date.games || [])
    .filter((game) => game.status?.abstractGameState === 'Final')
    .filter((game) => OFFICIAL_GAME_TYPES.has(game.gameType || 'R'))
    .map(mapScheduleGame);
}